import React from "react";
import { useI18n } from "../../contexts/I18nContext";
import { useAnimalType, ANIMAL_TYPES } from "../../contexts/AnimalTypeContext";
import { Trophy, Sparkles } from "lucide-react";

/**
 * Full-screen animal-type picker. Renders before the home dashboard the first time
 * a visitor opens the app (and any time they tap the type pill to change).
 */
export default function AnimalTypePicker({ onPick }) {
  const { t, lang } = useI18n();
  const { setType } = useAnimalType();
  const ar = lang === "ar";

  const pick = (key) => {
    setType(key);
    onPick && onPick(key);
  };

  return (
    <div className="min-h-screen mobile-dashboard px-5 py-10" dir={ar ? "rtl" : "ltr"} data-testid="animal-type-picker">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30 mb-3">
          <Sparkles className="h-3 w-3" />
          {ar ? "ابدأ من هنا" : "Start here"}
        </div>
        <h1 className="text-2xl font-black gold-text leading-tight">
          {ar ? "اختر اهتمامك" : "Pick your interest"}
        </h1>
        <p className="text-[12px] text-white/65 mt-2 max-w-xs mx-auto">
          {ar
            ? "حلال (إبل/خيل/صقور)، شعر، أو إنشاد — كل اختيار له لوحته الخاصة."
            : "Livestock, poetry, or chanting — each has its own dashboard."}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {ANIMAL_TYPES.map((a) => {
          const isPoet = a.key === "poet";
          const isChanter = a.key === "chanter";
          return (
            <button
              key={a.key}
              type="button"
              onClick={() => pick(a.key)}
              className="relative rounded-3xl p-5 flex flex-col items-center gap-2 hover:-translate-y-1 transition glass-gold border border-[#D4AF37]/30 group"
              data-testid={`atp-${a.key}`}
            >
              <div className="text-5xl leading-none mb-1" aria-hidden>{a.emoji}</div>
              <div className="text-base font-black gold-text">{ar ? a.ar : a.en}</div>
              <div className="flex items-center gap-1 mt-1 flex-wrap justify-center">
                {isPoet ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold bg-[#D4AF37] text-black">
                    <ScrollText className="h-2.5 w-2.5" />
                    {ar ? "شعر" : "Poetry"}
                  </span>
                ) : isChanter ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold bg-[#D4AF37] text-black">
                    <Mic2 className="h-2.5 w-2.5" />
                    {ar ? "إنشاد" : "Chanting"}
                  </span>
                ) : (
                  <>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold bg-[#D4AF37] text-black">
                      <Trophy className="h-2.5 w-2.5" />
                      {ar ? "مزاين" : "Mazayen"}
                    </span>
                  </>
                )}
              </div>
            </button>
          );
        })}
      </div>

      <div className="text-center mt-8 text-[11px] text-white/45">
        {ar ? "يمكنك تغيير نوع الحلال في أي وقت من أعلى الشاشة." : "You can change the type anytime from the top of the screen."}
      </div>
    </div>
  );
}
